import type { Dispatch, FC, ReactNode, SetStateAction } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';

import type { ActionItemTablePage } from 'src/types/myFollowUp';
import { useActionItemListQuery } from 'src/components/myFollowUp/MyFollowUpPage/FollowUpOverview/hooks';

interface FollowUpTableValue {
  actionItems: ActionItemTablePage;
  offset: number;
  setOffset: Dispatch<SetStateAction<number>>;
  ordering: string;
  setOrdering: Dispatch<SetStateAction<string>>;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  pageSize: number;
}

const emptyActionItems = {
  count: 0,
  results: [],
  next: '',
  previous: '',
};

const FollowUpTableContext = createContext<FollowUpTableValue>({
  actionItems: emptyActionItems,
  offset: 0,
  setOffset: () => {},
  ordering: '-id',
  setOrdering: () => {},
  loading: false,
  error: null,
  refetch: () => {},
  pageSize: 10,
});

interface FollowUpTableProviderProvider {
  pageSize?: number;
  children?: ReactNode;
}

export const FollowUpTableProvider: FC<FollowUpTableProviderProvider> = ({
  pageSize = 10,
  children,
}) => {
  const [offset, setOffset] = useState(0);
  const [ordering, setOrdering] = useState('-id');
  const {
    data,
    isFetching: loading,
    error,
    refetch,
  } = useActionItemListQuery(offset, ordering, pageSize);
  const actionItems = useMemo(() => (data ? data : emptyActionItems), [data]);

  return (
    <FollowUpTableContext.Provider
      value={{
        actionItems,
        offset,
        setOffset,
        ordering,
        setOrdering,
        loading,
        error,
        refetch,
        pageSize,
      }}
    >
      {children}
    </FollowUpTableContext.Provider>
  );
};

const useFollowUpTable = () => useContext(FollowUpTableContext);
export default useFollowUpTable;
